import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { useCreateKbCategory, useDeleteKbCategory, useKbCategories } from './queries';
import { Select } from '../../components/Select';

/**
 * Kategorien der Wissensdatenbank -- F4-09, K-109.
 *
 * **Als Dialog und nicht mehr als aufgeklappter Abschnitt in der
 * Trefferspalte.** Vorher sass die Pflege zwischen Filter und Treffern: Wer
 * suchte, sah dauerhaft ein Bedienelement fuer eine Aufgabe, die man einmal im
 * Quartal hat -- und die Trefferliste begann eine Bildschirmhoehe weiter
 * unten. Ein eigener Menuepunkt waere das andere Extrem; der Dialog liegt
 * dazwischen: erreichbar aus der Wissensdatenbank, sichtbar nur, wenn man ihn
 * oeffnet.
 */
export function CategoryManager() {
  const { t } = useTranslation();
  const categories = useKbCategories();
  const create = useCreateKbCategory();
  const remove = useDeleteKbCategory();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button>{t('knowledge.manageCategories')}</Button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content">
          <Dialog.Title asChild>
            <h2 style={{ marginBottom: 'var(--space-2)' }}>{t('knowledge.manageCategories')}</h2>
          </Dialog.Title>
          <Dialog.Description className="field__hint">
            {t('knowledge.categoriesHint')}
          </Dialog.Description>

          {categories.isError ? <ErrorNotice error={categories.error} /> : null}
          {remove.isError ? <ErrorNotice error={remove.error} /> : null}
          {create.isError ? <ErrorNotice error={create.error} /> : null}

          {categories.data?.length === 0 ? (
            <EmptyState icon="categories" title={t('knowledge.noCategories')} />
          ) : (
            <ul className="kb-files" style={{ margin: 'var(--space-4) 0' }}>
              {(categories.data ?? []).map((category) => (
                <li key={category.id} className="kb-file">
                  <span
                    className="kb-file__name"
                    style={{ paddingLeft: `calc(${String(category.depth)} * var(--space-4))` }}
                  >
                    {category.name}
                  </span>
                  <Button
                    variant="ghost"
                    aria-label={t('knowledge.deleteCategory', { name: category.name })}
                    onClick={() => {
                      remove.mutate(category.id);
                    }}
                  >
                    {t('masterdata.delete')}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="row">
            <input
              className="field__control"
              aria-label={t('masterdata.name')}
              placeholder={t('knowledge.categoryName')}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            <Select
              className="field__control"
              aria-label={t('knowledge.parentCategory')}
              value={parentId}
              onChange={(event) => {
                setParentId(event.target.value);
              }}
            >
              <option value="">{t('knowledge.topLevel')}</option>
              {(categories.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
            <Button
              variant="primary"
              disabled={!name.trim()}
              busy={create.isPending}
              onClick={() => {
                create.mutate(
                  { name: name.trim(), ...(parentId ? { parent_id: parentId } : {}) },
                  {
                    onSuccess: () => {
                      setName('');
                      setParentId('');
                    },
                  },
                );
              }}
            >
              {t('masterdata.add')}
            </Button>
          </div>

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 'var(--space-4)' }}>
            <Dialog.Close asChild>
              <Button variant="ghost">{t('app.close')}</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
